/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useMemo } from 'react';
import { useField } from 'informed';
import { Optional } from 'utility-types';
import cn from 'classnames';

import Dropdown from '../../components/Dropdown';
import DropdownCreatable from '../../components/DropdownCreatable';
import DropdownPaginate, { IDropdownPaginate } from '../../components/DropdownPaginate';
import { IDropdownOption } from '../../types';

export type IDropdownInformed = Optional<IDropdownPaginate, 'loadOptions'> & {
  asyncPaginate?: boolean;
  defaultValue?: IDropdownOption | IDropdownOption[] | string | string[];
  errorClassName?: string;
  isCreatable?: boolean;
  label?: string | JSX.Element;
  labelClassName?: string;
  name: string;
  options?: IDropdownOption[];
  storageValue?: string;
  onChange?: (value: IDropdownOption) => void;
  validate?: (value: unknown, values: any) => void | unknown;
};

const DropdownInformed: FC<IDropdownInformed> = ({
  asyncPaginate = false,
  defaultValue,
  errorClassName,
  isCreatable = false,
  label,
  labelClassName,
  name,
  options,
  storageValue,
  onChange,
  validate,
  ...rest
}) => {
  const {
    fieldApi: { setTouched, setValue, validate: validateField },
    fieldState: { error, showError, value },
    ref,
    render,
  } = useField<Partial<IDropdownInformed>, IDropdownOption>({
    defaultValue,
    name,
    showErrorIfError: true,
    type: 'select',
    validate,
  });

  const DropdownElement = useMemo(() => {
    if (asyncPaginate) {
      return DropdownPaginate;
    } else if (isCreatable) {
      return DropdownCreatable;
    }

    return Dropdown;
  }, [asyncPaginate, isCreatable]);

  const getStorageValueOption = (
    option: IDropdownOption | IDropdownOption[]
  ): string[] | string | false => {
    if (!storageValue) return false;

    // In multiselect case
    if (Array.isArray(option)) {
      return option?.map((element) => element[storageValue] || element);
    } else {
      return option[storageValue];
    }
  };

  return render(
    <DropdownElement
      error={
        showError ? (
          <p className={cn('hbh-select-error', errorClassName)}>{error as string}</p>
        ) : undefined
      }
      label={label && <label className={cn('hbh-select-label', labelClassName)}>{label}</label>}
      options={options}
      ref={ref}
      value={value ? value : null}
      onChange={(option) => {
        setValue(storageValue ? getStorageValueOption(option) : option);
        onChange?.(option);

        setTouched(true);
        validateField();
      }}
      {...rest}
    />
  );
};

export default DropdownInformed;
